/**
 * useResumableUpload — PR 20.
 *
 * Drives a browser-side resumable upload against the backend
 * /api/edgemail/admin/migration/upload-session/* endpoints (see
 * backend MigrationUploadSessionService).
 *
 * Behavior:
 *   - File is split into fixed-size chunks (server picks the size; client
 *     just respects what `init` returned).
 *   - Each chunk is sent as a raw `application/octet-stream` PUT to
 *     `/upload-session/:sessionId/chunks/:chunkIndex`.
 *   - On a transient PUT failure the same chunk is retried with bounded
 *     exponential backoff. The server is idempotent for an already-written
 *     chunk so retries are safe.
 *   - The session_id + last_chunk_index are persisted in `localStorage` keyed
 *     by a file fingerprint (name + size + last-modified) so a page reload
 *     can pick the upload back up. Resume reconciles with the server's
 *     authoritative `bytes_received` to avoid double-uploading.
 *   - A `BroadcastChannel` named `edgemail-uploads` posts progress and state
 *     so a second tab can mirror progress and warn against duplicate starts.
 *
 * Returns:
 *   - state — { phase, progress, error, session, ... }
 *   - start(file) — initiates a new session (or auto-resumes by fingerprint).
 *   - abort()    — cancels the current upload + tells the server to clean up.
 *
 * Concurrency:
 *   We send chunks serially to keep the simple in-order server contract. PST
 *   uploads dwarf the request roundtrip cost so parallelism would add little.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { EdgeMailAdminAPI } from '../services/EdgeMailAdminAPI';
import type {
  UploadSession,
  UploadSourceKind,
} from '../types/migration.types';

type Phase = 'idle' | 'initializing' | 'uploading' | 'finalizing' | 'done' | 'error' | 'aborted';

export interface UploadHookState {
  phase: Phase;
  progress: number;                  // 0..1
  bytesReceived: number;
  totalBytes: number;
  session: UploadSession | null;
  error: string | null;
  filename: string | null;
}

interface StartArgs {
  file: File;
  kind: UploadSourceKind;
}

interface PersistedRecord {
  session_id: string;
  fingerprint: string;
  filename: string;
  total_size: number;
  next_chunk_index: number;
  bytes_received: number;
  ts: number;
}

const RESUME_KEY_PREFIX = 'edgemail-upload:';
const BROADCAST_CHANNEL = 'edgemail-uploads';
const MAX_CHUNK_RETRIES = 5;
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000];

function fingerprintFor(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}
function recordKey(fp: string): string {
  return RESUME_KEY_PREFIX + fp;
}
function loadRecord(fp: string): PersistedRecord | null {
  try {
    const raw = localStorage.getItem(recordKey(fp));
    return raw ? (JSON.parse(raw) as PersistedRecord) : null;
  } catch {
    return null;
  }
}
function saveRecord(rec: PersistedRecord): void {
  try {
    localStorage.setItem(recordKey(rec.fingerprint), JSON.stringify(rec));
  } catch {
    /* quota — ignore, it's just a hint */
  }
}
function dropRecord(fp: string): void {
  try { localStorage.removeItem(recordKey(fp)); } catch { /* ignore */ }
}

function classifyError(err: unknown): { message: string; transient: boolean } {
  // axios error shape; falling back to plain Error
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  const status: number | undefined = e?.response?.status;
  const code: string | undefined = e?.response?.data?.error?.code;
  const msg: string =
    e?.response?.data?.error?.message ||
    e?.message ||
    'Upload failed';

  // 429 + 5xx + network errors are retryable.
  const transient =
    status === 429 ||
    (typeof status === 'number' && status >= 500 && status < 600) ||
    !status;

  // E_OUT_OF_ORDER means the server has further than us; not transient on its
  // own but caller should re-sync from server.
  if (code === 'E_OUT_OF_ORDER') return { message: msg, transient: false };
  return { message: msg, transient };
}

export function useResumableUpload() {
  const [state, setState] = useState<UploadHookState>({
    phase: 'idle',
    progress: 0,
    bytesReceived: 0,
    totalBytes: 0,
    session: null,
    error: null,
    filename: null,
  });

  const abortFlag = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(BROADCAST_CHANNEL);
    channelRef.current = ch;
    return () => { ch.close(); channelRef.current = null; };
  }, []);

  const broadcast = useCallback((payload: Partial<UploadHookState> & { type: string }) => {
    channelRef.current?.postMessage(payload);
  }, []);

  const start = useCallback(async ({ file, kind }: StartArgs): Promise<UploadSession | null> => {
    abortFlag.current = false;
    const fp = fingerprintFor(file);
    const prior = loadRecord(fp);

    setState((s) => ({
      ...s,
      phase: 'initializing',
      progress: 0,
      bytesReceived: 0,
      totalBytes: file.size,
      session: null,
      error: null,
      filename: file.name,
    }));

    let session: UploadSession;
    try {
      if (prior) {
        try {
          session = await EdgeMailAdminAPI.getMigrationUploadSession(prior.session_id);
          if (session.state !== 'active' || session.total_size !== file.size) {
            // Stale or mismatched — start fresh.
            dropRecord(fp);
            session = await EdgeMailAdminAPI.createMigrationUploadSession({
              filename: file.name,
              total_size: file.size,
              kind,
              content_type: file.type || undefined,
            });
          }
        } catch {
          dropRecord(fp);
          session = await EdgeMailAdminAPI.createMigrationUploadSession({
            filename: file.name,
            total_size: file.size,
            kind,
            content_type: file.type || undefined,
          });
        }
      } else {
        session = await EdgeMailAdminAPI.createMigrationUploadSession({
          filename: file.name,
          total_size: file.size,
          kind,
          content_type: file.type || undefined,
        });
      }
    } catch (err) {
      const { message } = classifyError(err);
      setState((s) => ({ ...s, phase: 'error', error: message }));
      return null;
    }

    saveRecord({
      session_id: session.session_id,
      fingerprint: fp,
      filename: file.name,
      total_size: file.size,
      next_chunk_index: session.next_chunk_index,
      bytes_received: session.bytes_received,
      ts: Date.now(),
    });

    setState((s) => ({
      ...s,
      phase: 'uploading',
      session,
      bytesReceived: session.bytes_received,
      progress: file.size ? session.bytes_received / file.size : 0,
    }));
    broadcast({ type: 'started', session, filename: file.name, totalBytes: file.size });

    // Stream chunks from current next_chunk_index forward.
    const chunkSize = session.chunk_size;
    let idx = session.next_chunk_index;
    let bytes = session.bytes_received;
    let lastSession = session;

    while (bytes < file.size) {
      if (abortFlag.current) {
        setState((s) => ({ ...s, phase: 'aborted' }));
        return null;
      }
      const sliceStart = idx * chunkSize;
      const sliceEnd = Math.min(sliceStart + chunkSize, file.size);
      const blob = file.slice(sliceStart, sliceEnd);
      const buf = await blob.arrayBuffer();

      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          lastSession = await EdgeMailAdminAPI.appendMigrationUploadChunk(
            session.session_id,
            idx,
            buf,
          );
          break;
        } catch (err) {
          const { message, transient } = classifyError(err);
          if (!transient || attempt >= MAX_CHUNK_RETRIES) {
            setState((s) => ({ ...s, phase: 'error', error: message }));
            broadcast({ type: 'error', error: message });
            return null;
          }
          await new Promise((r) => setTimeout(r, BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]));
          attempt += 1;
        }
      }

      bytes = lastSession.bytes_received;
      idx = lastSession.next_chunk_index;

      saveRecord({
        session_id: session.session_id,
        fingerprint: fp,
        filename: file.name,
        total_size: file.size,
        next_chunk_index: idx,
        bytes_received: bytes,
        ts: Date.now(),
      });

      const nextProgress = file.size ? bytes / file.size : 0;
      setState((s) => ({
        ...s,
        session: lastSession,
        bytesReceived: bytes,
        progress: nextProgress,
      }));
      broadcast({ type: 'progress', bytesReceived: bytes, totalBytes: file.size, progress: nextProgress });
    }

    // Finalize
    setState((s) => ({ ...s, phase: 'finalizing' }));
    let finalized: UploadSession;
    try {
      finalized = await EdgeMailAdminAPI.finalizeMigrationUploadSession(session.session_id);
    } catch (err) {
      const { message } = classifyError(err);
      setState((s) => ({ ...s, phase: 'error', error: message }));
      broadcast({ type: 'error', error: message });
      return null;
    }

    dropRecord(fp);
    setState((s) => ({
      ...s,
      phase: 'done',
      session: finalized,
      bytesReceived: finalized.bytes_received,
      progress: 1,
    }));
    broadcast({ type: 'done', session: finalized });
    return finalized;
  }, [broadcast]);

  const abort = useCallback(async () => {
    abortFlag.current = true;
    const session = state.session;
    if (session && session.state === 'active') {
      try {
        await EdgeMailAdminAPI.abortMigrationUploadSession(session.session_id);
      } catch {
        /* best-effort */
      }
      // Strip the resume hint so a refresh doesn't try to re-attach.
      const fp = state.filename
        ? `${state.filename}|${state.totalBytes}|0`
        : null;
      if (fp) dropRecord(fp);
    }
    setState((s) => ({ ...s, phase: 'aborted' }));
    broadcast({ type: 'aborted' });
  }, [state.session, state.filename, state.totalBytes, broadcast]);

  return { state, start, abort };
}
